/**
 * Studio style presets (Phase 5 §4) — one-click caption looks the operator
 * applies as a starting point and then tunes.
 *
 *   • "Founding punch"  — the original Quotologies grammar, expressed as
 *     EXPLICIT values (derived from the LEGACY_* constants, never retyped)
 *     so applying it overrides any prior edits back to the founding look.
 *
 *   • "Clean uniform"   — the operator's reference look (competitor frames,
 *     2026-07-17): every line the SAME size, emphasis is COLOR only (warm
 *     gold phrase spans over white), tight leading + tracking, heavy
 *     geometric grotesque from the committed font library (Archivo Black —
 *     the heaviest, closest face to the reference's bold sans; Poppins-Bold
 *     is rounder/lighter), block-centered mid-frame.
 *
 * Applying a preset = ONE journal entry (source "inspector",
 * label "Applied preset: …") committed through the same path as any other
 * inspector edit — fully undoable, tunable after.
 *
 * Pure data module. The patch shape is validated in stylePresets.test.ts
 * through the same validateIteratePatch gate chat patches pass.
 */
import type { EditPatch } from "./clipEditState.ts";
import {
  DEFAULT_TEXT_SHADOW,
  DEFAULT_VIDEO_DIM,
  LEGACY_CAPTION_LAYOUT,
  LEGACY_RENDER_BRAND,
} from "../remotion/types.ts";

export interface StylePreset {
  id: string;
  label: string;
  /** One-line description shown under the button. */
  description: string;
  patch: EditPatch;
}

/** The reference's warm peachy gold (vs the founding saturated #FFBF17). */
export const REFERENCE_GOLD = "#E8A87C";

export const STYLE_PRESETS: StylePreset[] = [
  {
    id: "founding-punch",
    label: "Founding punch",
    description: "The original look — big gold punch line, cream body, one gold word per line.",
    patch: {
      layout: {
        caption: {
          captionStyle: "punch",
          fontSizePx: LEGACY_CAPTION_LAYOUT.fontSizePx,
          twoLine: { ...LEGACY_CAPTION_LAYOUT.twoLine! },
          lineHeight: LEGACY_CAPTION_LAYOUT.lineHeight,
          letterSpacing: LEGACY_CAPTION_LAYOUT.letterSpacing,
          sidePaddingPx: LEGACY_CAPTION_LAYOUT.sidePaddingPx,
          bottomPx: LEGACY_CAPTION_LAYOUT.bottomPx,
          justify: LEGACY_CAPTION_LAYOUT.justify,
          textShadow: LEGACY_CAPTION_LAYOUT.textShadow,
          box: null,
          // v2 + motion fields reset explicitly — the journal collapse is
          // field-level last-wins, so omitting them would let stroke /
          // karaoke / animations survive "back to the founding look".
          wrapMode: "authored",
          stroke: null,
          align: "center",
          textTransform: "none",
          opacity: 1,
          karaoke: null,
          animation: null,
        },
        // A raised darken layer is NOT the founding look either — reset it
        // (phase 3; value-identical to absent for untouched clips).
        video: { dim: DEFAULT_VIDEO_DIM },
      },
      brand: {
        captionFont: LEGACY_RENDER_BRAND.captionFont,
        punchFont: LEGACY_RENDER_BRAND.punchFont,
        captionColor: LEGACY_RENDER_BRAND.captionColor,
        captionAccent: LEGACY_RENDER_BRAND.captionAccent,
      },
    },
  },
  {
    id: "clean-uniform",
    label: "Clean uniform",
    description: "Reference look — equal-size stacked lines, heavy sans, warm gold phrase spans.",
    patch: {
      layout: {
        caption: {
          captionStyle: "uniform",
          // ~3.4% of the 3840px canvas per line — bold but stackable; the
          // operator tunes from here (per-line sizes are ignored in uniform).
          fontSizePx: 130,
          // Tight leading + tracking, per the reference frames.
          lineHeight: 1.05,
          letterSpacing: "-1px",
          sidePaddingPx: 200,
          justify: "center",
          bottomPx: 240,
          textShadow: DEFAULT_TEXT_SHADOW,
          box: null,
        },
      },
      brand: {
        captionFont: "Archivo Black",
        // Digit runs swap to punchFont — same family, so numbers don't
        // change face mid-line in the uniform look.
        punchFont: "Archivo Black",
        captionColor: "#FFFFFF",
        captionAccent: REFERENCE_GOLD,
      },
    },
  },
];
